import React, { useState } from 'react';
import { Modal, DatePicker, Button, Divider } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useMutation } from '@tanstack/react-query';
import { notification } from 'antd';
import { DownloadOutlined, CalendarOutlined } from '@ant-design/icons';
import './DownloadCallLogsModal.scss';
import { downloadCallLogs } from '@/api/reseller';

const { RangePicker } = DatePicker;

interface DownloadCallLogsModalProps {
	isOpen: boolean;
	onClose: () => void;
	clientId: string;
	clientName: string;
}

const DownloadCallLogsModal: React.FC<DownloadCallLogsModalProps> = ({
	isOpen,
	onClose,
	clientId,
	clientName,
}) => {
	const [startDate, setStartDate] = useState<number | null>(null);
	const [endDate, setEndDate] = useState<number | null>(null);

	const downloadCallLogsMutation = useMutation({
		mutationKey: ['downloadCallLogs'],
		mutationFn: downloadCallLogs,
		onError: (error) => {
			console.error('Download error:', error);
			notification.error({
				message: 'Download Failed',
				description: 'Failed to download call logs. Please try again.',
				placement: 'topRight',
			});
		},
	});

	const handleDateRangeChange = (_: any, dateStrings: [string, string]) => {
		const [start, end] = dateStrings;
		setStartDate(start ? dayjs(start).valueOf() : null);
		setEndDate(end ? dayjs(end).valueOf() : null);
	};

	const handleDownload = () => {
		downloadCallLogsMutation.mutate({
			startDate,
			endDate,
			clientName: clientId,
		});
	};

	const disabledDate = (current: Dayjs) => {
		return current && current.isAfter(dayjs().endOf('day'));
	};

	return (
		<Modal
			title={
				<div className="download-modal-title">
					<DownloadOutlined className="title-icon" />
					<span>Download Call Logs - {clientName}</span>
				</div>
			}
			open={isOpen}
			onCancel={onClose}
			width={700}
			className="download-call-logs-modal"
			footer={[
				<Button key="cancel" onClick={onClose}>
					Cancel
				</Button>,
				<Button
					key="download"
					type="primary"
					icon={<DownloadOutlined />}
					onClick={handleDownload}
					loading={downloadCallLogsMutation.isPending}
				>
					{downloadCallLogsMutation.isPending ? 'Downloading...' : 'Download Logs'}
				</Button>,
			]}
		>
			<div className="modal-content">
				<div className="date-range-section">
					<div className="section-header">
						<CalendarOutlined className="section-icon" />
						<span>Select Date Range</span>
					</div>
					<div className="date-picker-wrapper">
						<RangePicker
							className="custom-range-picker"
							onChange={handleDateRangeChange}
							placement="bottomRight"
							disabledDate={disabledDate}
						/>
					</div>
				</div>
				<Divider />
				<div className="info-section">
					<p className="info-text">
						• If no date range is selected, the call logs from the last month will be fetched from
						the current date.
					</p>
					<p className="info-text">• The downloaded file will be in CSV format</p>
					<p className="info-text">• Large date ranges may take longer to process</p>
				</div>
			</div>
		</Modal>
	);
};

export default DownloadCallLogsModal;
